import { boundNumberField, BoundSelectField, Css, FormPageLayout, useComputed } from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  ChangeEventBudgetPhase,
  ChangeEventType,
  ProjectReadyPlanConfigOrientation,
  SaveProjectReadyPlanConfigsInput,
  Stage,
} from "src/generated/graphqlTypes";
import {
  ConfigOptionsAndDelta,
  filterSelectedRposByOptionGroup,
  mapRecomputedCostToForm,
  mapSelectedRpoToForm,
  ProductOfferingConfigForm,
} from "src/routes/libraries/product-offering-configs/AddProductOfferingConfigPage";
import { AddProductOfferingConfig_ReadyPlanOptionsFragment } from "src/routes/libraries/product-offering-configs/AddProductOfferingConfigPage.generated";
import { CostSummarySideBar } from "src/routes/libraries/product-offering-configs/components/CostSummarySideBar";
import { OptionsByOptionGroupSection } from "src/routes/libraries/product-offering-configs/components/OptionsByOptionGroupSection";
import { ProductOfferingConfigApplyParams } from "src/routes/routesDef";
import { createChangeEventsUrl } from "src/RouteUrls";
import { queriesResult } from "src/utils";
import {
  ApplyProductOfferingConfig_ProjectFragment,
  ApplyProductOfferingConfig_SelectedQuery,
  ApplyProductOfferingConfigMetaQuery,
  useApplyProductOfferingConfig_SelectedQuery,
  useApplyProductOfferingConfigDefaultOptionsQuery,
  useApplyProductOfferingConfigMetaQuery,
  useApplyProductOfferingConfigPageMetaQuery,
  useApplyProductOfferingConfigs_DeltasLazyQuery,
  useApplyProductOfferingConfigsMutation,
  useCreateProductOfferingConfigChangeEventMutation,
} from "./ApplyProductOfferingConfigPage.generated";

export function ApplyProductOfferingConfigPage() {
  const { configId, projectId } = useParams<ProductOfferingConfigApplyParams>();
  // Grab the existing project information
  const setupQuery = useApplyProductOfferingConfigPageMetaQuery({ variables: { projectId } });

  // And then grab the default config information based on the configId
  const configQuery = useApplyProductOfferingConfig_SelectedQuery({
    variables: {
      id: configId,
      skipCheckoutConfig: !configId.startsWith("ccfg:"),
      skipProductOfferingConfig: !configId.startsWith("poc:"),
      skipProjectReadyPlanConfig: !configId.startsWith("prpc:"),
    },
  });
  // If we've loaded a config, we can extract out the product offering and RPOs to use for the meta query
  const [productOfferingId, rpoIds] = configId.startsWith("poc:")
    ? [
        configQuery.data?.productOfferingConfig?.productOffering.id!,
        configQuery.data?.productOfferingConfig?.readyPlanOptions.map((rpo) => rpo.id) ?? [],
      ]
    : configId.startsWith("ccfg:")
      ? [
          // Otherwise we've got a checkout config, so extract out the PO and RPOs from there
          configQuery.data?.checkoutConfig?.productOffering?.id!,
          configQuery.data?.checkoutConfig?.optionGroups.map((og) => og.selectedOption?.option.id).compact() || [],
        ]
      : [
          configQuery.data?.projectReadyPlanConfig?.readyPlan?.id!,
          configQuery.data?.projectReadyPlanConfig?.readyPlanOptions.map((rpo) => rpo.id) ?? [],
        ];

  const project = setupQuery.data?.project!;
  const changeEventReasons = setupQuery.data?.changeEventReasons ?? [];

  const newDefaultOptionsQuery = useApplyProductOfferingConfigDefaultOptionsQuery({
    variables: { productOfferingId, currentOptionIds: rpoIds },
    fetchPolicy: "no-cache",
    // To avoid having this query fire twice in the event we are loading an existing config, we will skip the this query until the first loads
    // The queriesResult helper will keep us in a loading state until both queries resolve
    skip: configQuery.loading || !productOfferingId,
  });
  const defaultRPOIds = newDefaultOptionsQuery.data?.calculateDefaultOfferingOptions.map((rpo) => rpo.id) ?? [];

  const metaDataQuery = useApplyProductOfferingConfigMetaQuery({
    variables: {
      costInput: { productOfferingId, readyPlanOptionIds: defaultRPOIds },
      programDataInput: { readyPlan: productOfferingId, readyPlanOptions: defaultRPOIds },
      productOfferingId,
    },
    fetchPolicy: "no-cache",
    // To avoid having this query fire twice in the event we are loading an existing config, we will skip the this query until the first loads
    // The queriesResult helper will keep us in a loading state until both queries resolve
    skip: configQuery.loading || !productOfferingId,
  });

  return queriesResult([metaDataQuery, configQuery, newDefaultOptionsQuery] as const, {
    data: (metaData, configData) => (
      <ApplyProductOfferingConfigPageView
        metaData={metaData}
        configData={configData}
        productOfferingId={productOfferingId}
        defaultReadyPlanOptionIds={defaultRPOIds}
        project={project}
        changeEventReasons={changeEventReasons}
      />
    ),
  });
}

type ApplyOfferingConfigPageProps = {
  metaData: ApplyProductOfferingConfigMetaQuery;
  configData: ApplyProductOfferingConfig_SelectedQuery;
  productOfferingId: string;
  defaultReadyPlanOptionIds: string[]; // This will be only if we are creating a new config from a checkout config, and will be the list of RPO ids to preselect
  project: ApplyProductOfferingConfig_ProjectFragment;
  changeEventReasons: { id: string; code: string }[]; // This is the list of change event reasons to use for the change event
};

export function ApplyProductOfferingConfigPageView(props: ApplyOfferingConfigPageProps) {
  const history = useHistory();
  const { metaData, configData, productOfferingId, project, changeEventReasons } = props;
  const constructionStage = project.stages.find((s) => s.stage.code === Stage.Construction);

  const formState = useFormState({
    config: formConfig,
    init: { input: props, map: (queryResults) => mapConfigToForm(queryResults) },
  });

  const [applyProductOfferingConfigs] = useApplyProductOfferingConfigsMutation();
  const [createProductOfferingConfigChangeEvent] = useCreateProductOfferingConfigChangeEventMutation();
  const [recomputeConfigCost] = useApplyProductOfferingConfigs_DeltasLazyQuery({ fetchPolicy: "cache-and-network" });

  // Store/set the latest delta updates for our list of RPOs
  const [deltaRpos, setDeltas] = useState<ConfigOptionsAndDelta[]>(
    metaData.computeProductOfferingCostsAndPrices.productOffering.options.map((rpo) => ({
      rpo,
      delta: metaData.computeProductOfferingCostsAndPrices.allOptions.find((d) => d.readyPlanOption.id === rpo.id),
    })),
  );

  // The full list of all available config RPOs to select, should stay static.
  // We only update the RPO delta cost if it changes based on the list of selected RPOS (via `recomputeCosts` util).
  // Group these by the Location if it has a Personalization Type, or by the RPO type if it doesn't.
  const groupedRposByRoomOrType = useMemo(() => {
    const groupedByRoomOrType = deltaRpos
      .sortBy((d) => d.rpo.location?.room?.id.substring(2) ?? d.rpo.type.order.toString())
      .groupBy((res) =>
        res.rpo.optionGroup.personalizationType?.name && res.rpo.location?.room?.name
          ? res.rpo.location.room.name
          : res.rpo.type.name,
      );

    return Object.entries(groupedByRoomOrType)
      .sortBy(([roomOrTypeName, rpos]) => {
        // Check if this is a room (has personalizationType and room name)
        const isRoom = rpos.some(
          (res) => res.rpo.optionGroup.personalizationType?.name && res.rpo.location?.room?.name,
        );
        return [
          isRoom ? 1 : 0, // "not a room" labels first (0), room labels second (1)
          isRoom ? 0 : (rpos[0]?.rpo.type.order ?? 1), // For non-rooms, sort by GOT order; for rooms, use 0 to maintain room name sorting
          roomOrTypeName, // Finally sort by name
        ];
      })
      .map(([roomOrTypeName, rpos]) => {
        const rposGroupedByLocation = rpos
          .sortBy((res) => res.rpo.location?.id.substring(2) ?? "0")
          .groupBy((res) => res.rpo.location?.name ?? "");

        const rposGroupdByLocationAndOptionGroup = Object.entries(rposGroupedByLocation).map(
          ([locationName, rpos2]) => {
            const rposGroupedByOptionGroup = rpos2
              .sortBy((res) => res.rpo.optionGroup.order)
              .groupBy((res) => res.rpo.optionGroup.name);

            return { rposGroupedByOptionGroup, locationName };
          },
        );

        return { rposGroupdByLocationAndOptionGroup, roomOrTypeName };
      });
  }, [deltaRpos]);

  const selectedRpos = useComputed(
    () => formState.value.readyPlanOptions.map((os) => os.readyPlanOptionId).compact(),
    [formState],
  );

  /**
   On each RPO select/deselect:
   - Query the calc for the total config cost (base offering & selected RPOs costs),
    RPO delta costs, and combined program data to calc Cost/SqFt.
   - Then update formState with the refetched values
   **/
  const recomputeCosts = useCallback(
    async (rpo: AddProductOfferingConfig_ReadyPlanOptionsFragment) => {
      // Update form with selected RPO
      formState.readyPlanOptions.set(
        mapSelectedRpoToForm(
          metaData.computeProductOfferingCostsAndPrices.productOffering.options,
          formState as unknown as ObjectState<ProductOfferingConfigForm>,
          rpo,
        ),
      );
      const selectedRpos = formState.value.readyPlanOptions.map((os) => os.readyPlanOptionId).compact();
      const res = await recomputeConfigCost({
        variables: {
          costInput: { productOfferingId, readyPlanOptionIds: selectedRpos },
          programDataInput: { readyPlan: productOfferingId, readyPlanOptions: selectedRpos },
        },
      });
      // Fetch computed delta/total costs
      setDeltas(
        metaData.computeProductOfferingCostsAndPrices.productOffering.options.map((rpo) => ({
          rpo,
          delta: res.data?.computeProductOfferingCostsAndPrices.allOptions.find((d) => d.readyPlanOption.id === rpo.id),
        })),
      );
      // And update formState costs
      formState.set(mapRecomputedCostToForm(res.data!));
    },
    [formState, metaData, recomputeConfigCost, productOfferingId],
  );

  const missingRequiredRpos = useComputed(() => {
    const requiredRposByGroup = metaData.computeProductOfferingCostsAndPrices.productOffering.options
      .filter((rpo) => rpo.active && rpo.optionGroup.required)
      .groupBy((rpo) => rpo.optionGroup.name);
    const selectedRpos = formState.value.readyPlanOptions.map((rpo) => rpo.readyPlanOptionId).compact();
    const missingRpoGroups = Object.keys(requiredRposByGroup).filter(
      (k) => !requiredRposByGroup[k].some((rpo) => selectedRpos.includes(rpo.id)),
    );
    if (missingRpoGroups.isEmpty) return;
    return `${missingRpoGroups.join(", ")} missing a required option selection`;
  }, [formState]);

  const redirectRoute = createChangeEventsUrl(props.project.id);

  const filteredGroupedRposByOptionGroup = filterSelectedRposByOptionGroup(groupedRposByRoomOrType, selectedRpos);

  // wrap totalCostInCents in a computed to ensure total cost is updated when the RPO is selected/unselected
  const rpoTotalCostInCents = useComputed(() => formState.totalCostInCents.value ?? 0, [formState]);

  return (
    <FormPageLayout
      formSections={[
        {
          title: "Details",
          rows: [
            { name: undefined },
            {
              reactNodeSubtitle:
                "Select options to configure and see cost updates on. Apply a configuration to impact selected project.",
            },
            {
              reactNode: (
                <BoundSelectField
                  label="Orientation"
                  field={formState.orientation}
                  options={orientationOptions}
                  getOptionValue={(o) => o.code}
                  getOptionLabel={(o) => o.name}
                />
              ),
            },
            {
              totalCostInCents: boundNumberField({ type: "cents", readOnly: true }),
              totalCostSqFt: boundNumberField({ type: "cents", label: "Cost/ Sq Ft", readOnly: true }),
            },
            {
              reactNodeHelperText: (
                <p css={Css.sm.gray700.$}>
                  Costs summaries are pulled from a mixture of cost sources, including some that are not awarded bids.
                </p>
              ),
            },
          ],
        },
        ...groupedRposByRoomOrType.flatMap(({ roomOrTypeName, rposGroupdByLocationAndOptionGroup }) => {
          return {
            title: roomOrTypeName,
            rows: [
              {
                reactNode: rposGroupdByLocationAndOptionGroup.map(({ rposGroupedByOptionGroup, locationName }) => (
                  <OptionsByOptionGroupSection
                    key={locationName}
                    groupName={locationName}
                    groupedRpos={rposGroupedByOptionGroup}
                    formState={formState as unknown as ObjectState<ProductOfferingConfigForm>}
                    recomputeCosts={recomputeCosts}
                  />
                )),
              },
            ],
          };
        }),
      ]}
      breadCrumb={{ label: "Change Events / Create New", href: "" }}
      formState={formState as unknown as ObjectState<ProductOfferingConfigForm>}
      pageTitle={`Create Change Event`}
      submitAction={{
        label: "Apply",
        onClick: async () => {
          if (constructionStage?.hasSignedContract) {
            await createProductOfferingConfigChangeEvent({
              variables: {
                input: {
                  projectStageId: constructionStage?.id,
                  readyPlanId: productOfferingId,
                  originalReadyPlanId: project.readyPlanConfig?.readyPlan?.id,
                  originalReadyPlanOptions: project.readyPlanConfig?.readyPlanOptions.map((rpo) => ({
                    readyPlanOptionId: rpo.id,
                  })),
                  proposedReadyPlanOptions: formState.value.readyPlanOptions
                    .filter((rpo) => rpo.readyPlanOptionId)
                    .map((rpo) => ({ readyPlanOptionId: rpo.readyPlanOptionId })),
                  proposedOrientation: formState.value.orientation,
                  checkoutConfigId: configData.checkoutConfig?.id,
                  title: `Change Configuration of POF ${productOfferingId} on ${project.name}`,
                  reasonId: changeEventReasons.find((reason) => reason.code === "RFP_EXECUTION")?.id ?? "cer:1", // Just default this for now to what the apply ReadyPlanConfig was using, they can change it before submitting
                  type: ChangeEventType.External,
                  budgetPhase: ChangeEventBudgetPhase.Prebid,
                },
              },
            });
          } else {
            // If we do NOT have a contract on this structure yet, just apply it direction
            await applyProductOfferingConfigs({
              variables: { input: mapFormToApplyProductConfigConfigInput(project.readyPlanConfig?.id!, formState) },
            });
          }

          return history.push(redirectRoute);
        },
        disabled: !!missingRequiredRpos && missingRequiredRpos,
      }}
      cancelAction={{ label: "Cancel", onClick: redirectRoute }}
      rightSideBar={[
        {
          icon: "dollar",
          render: () => (
            <CostSummarySideBar
              totalCostInCents={rpoTotalCostInCents}
              groupedRposByType={filteredGroupedRposByOptionGroup}
            />
          ),
        },
      ]}
    />
  );
}

type ApplyProductOfferingConfigForm = ProductOfferingConfigForm & {
  orientation: ProjectReadyPlanConfigOrientation | null | undefined;
};

const orientationOptions = [
  { code: ProjectReadyPlanConfigOrientation.Left, name: "Left" },
  { code: ProjectReadyPlanConfigOrientation.Right, name: "Right" },
];

export const formConfig: ObjectConfig<ApplyProductOfferingConfigForm> = {
  id: { type: "value" },
  productOfferingId: { type: "value", rules: [required] },
  readyPlanOptions: { type: "list", rules: [required], config: { readyPlanOptionId: { type: "value" } } },
  totalCostInCents: { type: "value" },
  totalCostSqFt: { type: "value" },
  orientation: { type: "value" },
};

export function mapConfigToForm(queryResults: ApplyOfferingConfigPageProps): ApplyProductOfferingConfigForm {
  const { metaData, configData, project, defaultReadyPlanOptionIds } = queryResults;
  const savedConfigRpoIds = defaultReadyPlanOptionIds ?? [];
  const {
    computeProductOfferingCostsAndPrices: { totalCostInCents, productOffering, allOptions },
    computeProgramData: { sellableSqft },
  } = metaData;

  // Determine initial orientation from:
  // 1. CheckoutConfig.isReversed (Left if reversed, Right if not)
  // 2. ProjectReadyPlanConfig.orientation
  // 3. Existing project's readyPlanConfig.orientation
  // 4. Default to Right
  const getInitialOrientation = (): ProjectReadyPlanConfigOrientation => {
    if (configData.checkoutConfig) {
      return configData.checkoutConfig.isReversed
        ? ProjectReadyPlanConfigOrientation.Left
        : ProjectReadyPlanConfigOrientation.Right;
    }
    if (configData.projectReadyPlanConfig) {
      return configData.projectReadyPlanConfig.orientation;
    }
    // Fall back to project's existing PRPC orientation, or default to Right
    return project.readyPlanConfig?.orientation ?? ProjectReadyPlanConfigOrientation.Right;
  };

  return {
    productOfferingId: productOffering.id,
    readyPlanOptions: allOptions
      // If RPOs have been saved to the config, preselect its checkbox
      .map((deltaRpo) => ({
        readyPlanOptionId: savedConfigRpoIds?.find((optId) => optId === deltaRpo.readyPlanOption.id),
      })),
    // TBD: On intial render, if the BE config totalCostInCents is defined
    // default to the BE config totalCostInCents
    totalCostInCents,
    totalCostSqFt: sellableSqft ? Math.round(totalCostInCents / Number(sellableSqft)) : 0,
    orientation: getInitialOrientation(),
  };
}

export function mapFormToApplyProductConfigConfigInput(
  structureProjectConfigId: string,
  formState: ObjectState<ApplyProductOfferingConfigForm>,
): SaveProjectReadyPlanConfigsInput {
  // The totalCost fields are frontend-only, and we really only care about the productOfferingId and readyPlanOptions when applying a config
  const { productOfferingId, readyPlanOptions, orientation } = formState.value;
  const options = readyPlanOptions?.map((opt) => ({ readyPlanOptionId: opt.readyPlanOptionId })).compact() ?? [];

  return {
    projectReadyPlanConfigs: [
      {
        id: structureProjectConfigId,
        readyPlanId: productOfferingId,
        orientation,
        options: options
          .filter((opt) => opt.readyPlanOptionId)
          .map((opt) => ({ readyPlanOptionId: opt.readyPlanOptionId })),
      },
    ],
    overwriteProgramDataWithComputed: true,
  };
}
